import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import isEqual from 'lodash/isEqual';

import type { AutocompleteOptionProps, DateValues } from '@p81-common/ui';

import {
  ClearButton,
  SelectDropDown,
  DatePicker
} from 'components/common/Elements/Elements';

import { useNetworkRolloutDataGrid } from '../../helpers/useNetworkRolloutDataGrid';
import { createFilterCountry, createFilterFeatureAdoption } from 'utils/common';
import { validateDatesValues } from '../../helpers/helpers';

import classes from './FiltersBlock.module.scss';
import AutocompleteFilter from './AutocompleteFilter';
import TenantAutocompleteFilter from './TenantAutocompleteFilter';

type FiltersBlockProps = Pick<
  ReturnType<typeof useNetworkRolloutDataGrid>,
  | 'filter'
  | 'setFilter'
  | 'startFilter'
  | 'setStartFilter'
  | 'filterButton'
  | 'filters'
  | 'selectLoader'
  | 'trigerResetDatePicker'
  | 'resetAllfilters'
  | 'setVisibleFilterSave'
  | 'setVisibleFilterEdit'
  | 'changeFilter'
>;

const FiltersBlock = ({
  filter,
  setFilter,
  startFilter,
  setStartFilter,
  filters,
  selectLoader,
  resetAllfilters,
  filterButton,
  setVisibleFilterSave,
  setVisibleFilterEdit,
  changeFilter,
  trigerResetDatePicker
}: FiltersBlockProps) => {
  const { t } = useTranslation(['activity']);
  const [autocompleteOptionsMultiple, setAutocompleteOptionsMultiple] =
    useState<AutocompleteOptionProps[]>([]);
  const [tenantOptionsMultiple, setTenantOptionsMultiple] = useState<
    AutocompleteOptionProps[]
  >([]);

  const numberOfFilters = 19;
  const networkRegionsCountry: never[] = [];
  const fetureAdoption: never[] = [];

  const handleFilter = (e: any) => {
    setFilter({ ...filter, [e.target]: e.value, currentPage: 1 });
    setVisibleFilterSave('button');
    setStartFilter(!startFilter);
    if (changeFilter !== 'Default') {
      setVisibleFilterEdit(true);
    }
  };

  function checkAndSetFilter(key: keyof typeof filter, value: any) {
    if (!isEqual(filter[key], value)) {
      setFilter({ ...filter, [key]: value, currentPage: 1 });
      setStartFilter(!startFilter);
      setVisibleFilterSave('button');
      if (changeFilter !== 'Default') {
        setVisibleFilterEdit(true);
      }
    }
  }

  const handleDatePickerChange =
    (key: keyof typeof filter) => (value: DateValues) => {
      checkAndSetFilter(key, value);
    };

  (function () {
    createFilterCountry(filters.regionCountry, networkRegionsCountry);
    createFilterFeatureAdoption(filters.featureAdoption, fetureAdoption);
  })();

  const resetFilters = () => {
    setAutocompleteOptionsMultiple([]);
    setTenantOptionsMultiple([]);
    resetAllfilters(false, true, false, true);
  };

  return (
    <div className={cn(filterButton ? classes.filters : classes.filters_close)}>
      <SelectDropDown
        options={
          Array.isArray(filters.statusFailed)
            ? filters.statusFailed.map((item: boolean) =>
                item
                  ? t('activity:filters.NETWORK_WITH_FAILED_INSTANCES')
                  : t('activity:filters.HEALTHY_NETWORKS')
              )
            : []
        }
        input={{ value: filter.statusFailed, onChange: handleFilter }}
        name={'statusFailed'}
        label={t('activity:filters.NETWORK_FAILURE_STATUS')}
        reload
        closeTrigger={{
          trigger: startFilter,
          setTrigger: setStartFilter
        }}
        selectLoader={selectLoader}
      />
      <SelectDropDown
        options={filters.networkVersion}
        input={{ value: filter.networkVersion, onChange: handleFilter }}
        name={'networkVersion'}
        label={t('activity:filters.NETWORK_VERSION')}
        multi
        renderKey={'label'}
        reload
        closeTrigger={{
          trigger: startFilter,
          setTrigger: setStartFilter
        }}
        selectLoader={selectLoader}
      />
      <SelectDropDown
        options={filters.networkGwMinVersion}
        input={{ value: filter.networkGwMinVersion, onChange: handleFilter }}
        name={'networkGwMinVersion'}
        label={t('activity:filters.GW_MIN_VERSION')}
        multi
        renderKey={'label'}
        reload
        closeTrigger={{
          trigger: startFilter,
          setTrigger: setStartFilter
        }}
        selectLoader={selectLoader}
      />
      <SelectDropDown
        options={filters.providerRegionName}
        input={{ value: filter.providerRegionName, onChange: handleFilter }}
        name={'providerRegionName'}
        label={t('activity:filters.NET_PROVIDER_REGION_NAME')}
        multi
        renderKey={'label'}
        reload
        closeTrigger={{
          trigger: startFilter,
          setTrigger: setStartFilter
        }}
        selectLoader={selectLoader}
      />
      <SelectDropDown
        options={networkRegionsCountry}
        input={{ value: filter.regionCountry, onChange: handleFilter }}
        name={'regionCountry'}
        label={t('activity:filters.NET_REGION_COUNTRY')}
        searchKeys={['value', 'findName']}
        renderKey={'fullName'}
        checkRenderKey={'fullName'}
        multi
        searchable
        closeTrigger={{
          trigger: startFilter,
          setTrigger: setStartFilter
        }}
        reload
        selectLoader={selectLoader}
      />
      <SelectDropDown
        options={fetureAdoption}
        input={{ value: filter.featureAdoption, onChange: handleFilter }}
        name={'featureAdoption'}
        label={t('activity:filters.FEATURE_ADOPTION')}
        multi
        renderKey={'label'}
        reload
        closeTrigger={{
          trigger: startFilter,
          setTrigger: setStartFilter
        }}
        selectLoader={selectLoader}
      />
      <SelectDropDown
        options={filters['tunnels.tunnelType']}
        input={{ value: filter['tunnels.tunnelType'], onChange: handleFilter }}
        name={'tunnels.tunnelType'}
        label={t('activity:filters.TUNNEL_TYPE')}
        multi
        renderKey={'label'}
        reload
        closeTrigger={{
          trigger: startFilter,
          setTrigger: setStartFilter
        }}
        selectLoader={selectLoader}
      />
      <SelectDropDown
        options={filters.status}
        input={{ value: filter.status, onChange: handleFilter }}
        name={'status'}
        label={t('activity:filters.CUSTOMER_STATUS')}
        multi
        renderKey={'label'}
        reload
        closeTrigger={{
          trigger: startFilter,
          setTrigger: setStartFilter
        }}
        selectLoader={selectLoader}
      />
      <SelectDropDown
        options={filters.partnerType}
        input={{ value: filter.partnerType, onChange: handleFilter }}
        name={'partnerType'}
        label={t('activity:filters.PARTNER_TYPE')}
        multi
        renderKey={'label'}
        reload
        closeTrigger={{
          trigger: startFilter,
          setTrigger: setStartFilter
        }}
        selectLoader={selectLoader}
      />
      <SelectDropDown
        options={filters.companySize}
        input={{ value: filter.companySize, onChange: handleFilter }}
        name={'companySize'}
        label={t('activity:filters.COMPANY_SIZE')}
        multi
        renderKey={'label'}
        reload
        closeTrigger={{
          trigger: startFilter,
          setTrigger: setStartFilter
        }}
        selectLoader={selectLoader}
      />
      <SelectDropDown
        options={filters.plan}
        input={{ value: filter.plan, onChange: handleFilter }}
        name={'plan'}
        label={t('activity:filters.PRICING_PLAN')}
        multi
        renderKey={'label'}
        reload
        closeTrigger={{
          trigger: startFilter,
          setTrigger: setStartFilter
        }}
        selectLoader={selectLoader}
      />
      <SelectDropDown
        options={filters.billingCycle}
        input={{ value: filter.billingCycle, onChange: handleFilter }}
        name={'billingCycle'}
        label={t('activity:filters.BILLING_CYCLE')}
        multi
        renderKey={'label'}
        reload
        closeTrigger={{
          trigger: startFilter,
          setTrigger: setStartFilter
        }}
        selectLoader={selectLoader}
      />
      <SelectDropDown
        options={filters.healthStatus}
        input={{ value: filter.healthStatus, onChange: handleFilter }}
        name={'healthStatus'}
        label={t('activity:filters.HEALTH_STATUS')}
        multi
        renderKey={'label'}
        reload
        closeTrigger={{
          trigger: startFilter,
          setTrigger: setStartFilter
        }}
        selectLoader={selectLoader}
      />
      <SelectDropDown
        options={filters.industry}
        input={{ value: filter.industry, onChange: handleFilter }}
        name={'industry'}
        label={t('activity:filters.INDUSTRY')}
        multi
        renderKey={'label'}
        reload
        closeTrigger={{
          trigger: startFilter,
          setTrigger: setStartFilter
        }}
        selectLoader={selectLoader}
      />
      <SelectDropDown
        options={filters.customerSuccessEngineer}
        input={{
          value: filter.customerSuccessEngineer,
          onChange: handleFilter
        }}
        name={'customerSuccessEngineer'}
        label={t('activity:filters.CS_ENGINEER')}
        multi
        renderKey={'label'}
        reload
        closeTrigger={{
          trigger: startFilter,
          setTrigger: setStartFilter
        }}
        selectLoader={selectLoader}
      />
      <SelectDropDown
        options={filters.accountManager}
        input={{ value: filter.accountManager, onChange: handleFilter }}
        name={'accountManager'}
        label={t('activity:filters.ACCOUNT_MANAGER_')}
        multi
        renderKey={'label'}
        reload
        closeTrigger={{
          trigger: startFilter,
          setTrigger: setStartFilter
        }}
        selectLoader={selectLoader}
      />
      <DatePicker
        label={t('activity:filters.FIRST_PAYMENT')}
        input={{
          value: validateDatesValues(filter.firstPaymentDate),
          onChange: handleDatePickerChange('firstPaymentDate')
        }}
        trigerResetDatePicker={trigerResetDatePicker}
      />
      <DatePicker
        label={t('activity:filters.FIRST_INVOICE')}
        input={{
          value: validateDatesValues(filter.firstInvoiceAt),
          onChange: handleDatePickerChange('firstInvoiceAt')
        }}
        trigerResetDatePicker={trigerResetDatePicker}
      />
      <DatePicker
        label={t('activity:filters.NEXT_BILLING')}
        input={{
          value: validateDatesValues(filter.nextBillingDate),
          onChange: handleDatePickerChange('nextBillingDate')
        }}
        trigerResetDatePicker={trigerResetDatePicker}
      />
      <SelectDropDown
        options={filters.not_networkGwMinVersion}
        input={{
          value: filter.not_networkGwMinVersion,
          onChange: handleFilter
        }}
        name={'not_networkGwMinVersion'}
        label={t('activity:filters.NOT_GW_MIN_VERSION')}
        multi
        renderKey={'label'}
        reload
        closeTrigger={{
          trigger: startFilter,
          setTrigger: setStartFilter
        }}
        selectLoader={selectLoader}
      />
      <SelectDropDown
        options={filters.not_providerRegionName}
        input={{ value: filter.not_providerRegionName, onChange: handleFilter }}
        name={'not_providerRegionName'}
        label={t('activity:filters.NOT_NET_PROVIDER_REGION_NAME')}
        multi
        renderKey={'label'}
        reload
        closeTrigger={{
          trigger: startFilter,
          setTrigger: setStartFilter
        }}
        selectLoader={selectLoader}
      />
      <AutocompleteFilter
        autocompleteOptionsMultiple={autocompleteOptionsMultiple}
        setAutocompleteOptionsMultiple={setAutocompleteOptionsMultiple}
        handleFilter={handleFilter}
      />
      <TenantAutocompleteFilter
        autocompleteOptionsMultiple={tenantOptionsMultiple}
        setAutocompleteOptionsMultiple={setTenantOptionsMultiple}
        handleFilter={handleFilter}
      />
      <ClearButton
        className={classes.clear}
        onClick={resetFilters}
        numberОfFilters={numberOfFilters}
      />
    </div>
  );
};

export default FiltersBlock;
